<script>
import { isEmail, isInt, isNumber, isURL, isUUID } from 'class-validator';
import * as core from '@nova/core';
import { DateTime } from 'luxon';
import { reactive } from 'vue';
import { $t } from '@satellite/plugins/translator.js';

const validator = reactive({
  errors: reactive({}),
  rules: {
    required(fieldName = '') {
      return [
        v => {
          const errorString = fieldName
            ? `${fieldName} ${$t('global.basicTerms.required')}`
            : $t('global.errors.thisFieldIsRequired');

          return !!v || isNumber(v) || errorString;
        }
      ];
    },
    password: [
      v =>
        core.getPasswordValidationErrors(v).filter(x => x.passed === false).length === 0 ||
        $t('global.errors.passwordDoesNotMeetRequirements')
    ],
    email: [v => !v || isEmail(v) || $t('global.errors.emailMustBeValid')],
    phone: [v => !v || core.isPhoneValid(v) || $t('global.errors.pleaseEnterAValidPhoneNumber')],
    url: [v => !v || isURL(v) || $t('global.errors.pleaseEnterAValidUrl')],
    customField: function (
      fieldName,
      required,
      type,
      minLengthOrValue = null,
      maxLengthOrValue = null
    ) {
      let rules = this[type] || [];
      if (required) {
        rules = [...rules, ...this.required(fieldName)];
      }

      // TODO: Ensure validation actually makes sense for the type
      // so when we change the type of a field, clear the validations instead of only adding new ones
      if (isInt(minLengthOrValue) || isInt(maxLengthOrValue)) {
        // Unfortunately, this validator doesn't have access to novaCore in order to use the customField constant
        if (type === 'str' || type === 'bigstr') {
          rules = [...rules, ...this.optionalLengthBetween(minLengthOrValue, maxLengthOrValue)];
        } else if (type === 'int') {
          rules = [...rules, ...this.optionalNumberBetween(minLengthOrValue, maxLengthOrValue)];
        }
      }

      return rules;
    },
    genericRequired: [v => !!v || $t('global.errors.thisFieldIsRequired')],
    selectAtLeast: function (count = 0, itemName = 'item') {
      return [
        v =>
          (Array.isArray(v) && v.length >= count) ||
          $t('global.errors.pleaseSelectAtLeast', count, { requiredCount: count, itemName })
      ];
    },
    selectLimit: function (limit = 0, itemName = 'item') {
      return [
        v =>
          (Array.isArray(v) && v.length <= limit) ||
          $t('global.errors.cannotSelectMoreThan', limit, { limit, itemName })
      ];
    },
    eq: function (comp = '', message = null) {
      return [
        v => v === comp || `${message || $t('global.errors.fieldMustEqual', { value: comp })}`
      ];
    },
    time: [
      v => {
        let parts = v.split(':');
        let hour = parts[0].substring(0, 2);
        let minute = parts?.[1];
        if (hour < 1 || hour > 12) {
          return $t('global.errors.invalidHour');
        }
        if (minute < 0 || minute > 59) {
          return $t('global.errors.invalidMinute');
        }
        return true;
      }
    ],
    time24: [
      v => {
        let parts = v.split(':');
        let hour = parts[0].substring(0, 2);
        let minute = parts?.[1];
        if (hour < 0 || hour > 23) {
          return $t('global.errors.invalidHour');
        }
        if (minute < 0 || minute > 59) {
          return $t('global.errors.invalidMinute');
        }
        return true;
      }
    ],
    length: function (len) {
      return [v => v.length === len || $t('global.errors.mustBeXCharacters', len, { length: len })];
    },
    lengthBetween: function (min, max) {
      return [
        v =>
          (v.length >= min && v.length <= max) ||
          $t('global.errors.mustBeBetweenXAndYCharactersLong', { min, max })
      ];
    },
    optionalLengthBetween: function (min, max) {
      const hasMin = min !== null && min !== undefined && min !== '';
      const hasMax = max !== null && max !== undefined && max !== '';
      if (hasMin && hasMax) {
        return [
          v =>
            !v ||
            (v.length >= min && v.length <= max) ||
            $t('global.errors.mustBeBetweenXAndYCharactersLong', { min, max })
        ];
      } else if (hasMin && !hasMax) {
        return [
          v => !v || v.length >= min || $t('global.errors.mustBeAtLeastXCharacters', min, { min })
        ];
      } else if (!hasMin && hasMax) {
        return [
          v => !v || v.length <= max || $t('global.errors.mustBeAtMostXCharacters', max, { max })
        ];
      }
    },
    minLength: function (min) {
      return [v => v.length >= min || $t('global.errors.mustBeAtLeastXCharacters', min, { min })];
    },
    numberBetween: function (min, max) {
      return [
        v => (v >= min && v <= max) || $t('global.errors.mustBeNumberBetweenXAndY', { min, max })
      ];
    },
    optionalNumberBetween: function (min, max) {
      const hasMin = min !== null && min !== undefined && min !== '';
      const hasMax = max !== null && max !== undefined && max !== '';
      if (hasMin && hasMax) {
        return [
          v =>
            v === null ||
            v === undefined ||
            v === '' ||
            (v >= min && v <= max) ||
            $t('global.errors.mustBeNumberBetweenXAndY', { min, max })
        ];
      } else if (hasMin && !hasMax) {
        return [
          v =>
            v === null ||
            v === undefined ||
            v === '' ||
            v >= min ||
            $t('global.errors.mustBeNumberGreaterThan', { min })
        ];
      } else if (!hasMin && hasMax) {
        return [
          v =>
            v === null ||
            v === undefined ||
            v === '' ||
            v <= max ||
            $t('global.errors.mustBeNumberLessThan', { max })
        ];
      }
    },
    number: [v => !isNaN(v) || $t('global.errors.mustBeANumber')],
    positiveInteger: function (customErrorMessage) {
      return [
        v =>
          !v ||
          (isInt(Number(v)) && Number(v) > 0) ||
          `${customErrorMessage || $t('global.errors.mustBeAPositiveInteger')}`
      ];
    },
    uuid: [value => !value || (Boolean(value) && isUUID(value)) || $t('global.errors.invalidUuid')],
    isCurrentOrFutureDate: [
      v => {
        return !this.novaCore.isPastDate(v) || $t('global.errors.dateMustNotBeInThePast');
      }
    ],
    isTimeWithin24Hours: function (
      startTime,
      message = $t('global.errors.mustBeWithin24HoursOfStartTime')
    ) {
      return [
        v => {
          const dateTime = DateTime.fromISO(startTime);
          const val = DateTime.fromISO(v);
          const isWithin24Hours =
            dateTime.minus({ hours: 24 }) <= val && dateTime.plus({ hours: 24 }) >= val;
          return isWithin24Hours || message;
        }
      ];
    }
  },

  get(fieldKey) {
    return this.errors[fieldKey];
  },
  add(error) {
    this.errors[error.field] = error.msg;
  },
  clear(field = null) {
    if (field) {
      delete this.errors[field];
    } else {
      this.errors = {};
    }
  }
});
export default validator;
</script>
